/**
 * Which input made the focus ring appear.
 *
 * Round 60 part 6. The browser's own :focus-visible heuristic counts a lone modifier keydown
 * as keyboard interaction, so pressing Cmd, Shift, Alt or Ctrl turns the ring on for whatever
 * is focused. Two everyday cases hit it: taking a screenshot with cmd + shift, and cmd + tab
 * away and back. A ring appearing because someone reached for the screenshot shortcut is noise.
 *
 * So modality is tracked explicitly. A navigation key turns it on; a pointer turns it off; a
 * modifier on its own does neither, leaving whatever was already true. data-rubato-modality on
 * the root is what the stylesheet reads, and :focus-visible is still required alongside it, so
 * this narrows the ring and never widens it.
 *
 * Keys that count as navigation are the ones that move or activate focus. Typing into a field
 * is not navigation, which is why letters are absent: a text input shows its own caret and does
 * not need a ring to explain itself.
 */

const NAVIGATION_KEYS = new Set([
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Enter',
  ' ',
  'Spacebar',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Escape',
]);

/* A lone modifier press never changes the modality. Listed rather than inferred from
   event.key.length, because that test would also swallow every single character key. */
const MODIFIERS = new Set(['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'OS', 'AltGraph', 'Fn']);

const root = document.documentElement;

function setModality(value) {
  if (root.dataset.rubatoModality === value) return;
  root.dataset.rubatoModality = value;
}

/* Capture phase, so the modality is already correct by the time the focus handler that
   follows the key press runs. */
document.addEventListener(
  'keydown',
  (event) => {
    if (MODIFIERS.has(event.key)) return;
    if (!NAVIGATION_KEYS.has(event.key)) return;
    setModality('keyboard');
  },
  true
);

for (const type of ['pointerdown', 'mousedown', 'touchstart']) {
  document.addEventListener(type, () => setModality('pointer'), true);
}

/* Start as pointer: a page that has just loaded has had no keyboard interaction, and the first
   Tab will say so. */
setModality('pointer');

/**
 * Round 61 part 19: coming back to a page does not count as reaching for the keyboard.
 *
 * Going to checkout and pressing Back left a focus ring around CART. Two things line up to
 * cause it. The browser restores focus to the control that was last focused, which is the cart
 * trigger, because that is what opened the drawer the customer checked out from. And this file
 * does not run again on a restore: bfcache keeps the page's JavaScript state, so the modality
 * stays on whatever it was when the page was frozen, which is keyboard if the customer had
 * pressed Enter or Space to open the cart. Restored focus plus a stale modality is a ring
 * around something nobody is looking at.
 *
 * So a restore resets the modality to pointer, the same state a fresh load starts in, and
 * takes the focus off a header control if the browser put it there. Only a header control:
 * focus restored into a form field is the browser being helpful and is left alone.
 *
 * Nothing is lost for a keyboard user. The next Tab sets the modality back and the ring
 * returns on the next control, which is what the regression test checks alongside the box
 * being gone.
 */
const HEADER_CONTROLS = '#header-component a, #header-component button, #header-component summary, header-actions a, header-actions button';

window.addEventListener('pageshow', () => {
  setModality('pointer');

  const active = document.activeElement;
  if (!active || active === document.body) return;
  if (!active.matches?.(HEADER_CONTROLS)) return;
  active.blur?.();
});
